import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styles from './DocumentProject.module.css';

const statuses = [
  { value: 'just-starting', label: 'Just Starting', emoji: '🌱', color: '#52D68A' },
  { value: 'in-progress',   label: 'In Progress',   emoji: '⚗️', color: '#38BDF8' },
  { value: 'hit-a-wall',    label: 'Hit a Wall',    emoji: '🧱', color: '#F87171' },
  { value: 'breakthrough',  label: 'Breakthrough',  emoji: '💡', color: '#F59E0B' },
  { value: 'completed',     label: 'Completed',     emoji: '✅', color: '#A78BFA' },
];

const WORD_GOAL = 100;

const DocumentProject = () => {
  const [user, setUser] = useState(null);
  const [title, setTitle] = useState('');
  const [field, setField] = useState('');
  const [status, setStatus] = useState('');
  const [story, setStory] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const saved = sessionStorage.getItem('labora_user');
    if (saved) {
      const u = JSON.parse(saved);
      setUser(u);
      if (u.field) setField(u.field);
    }
  }, []);

  const wordCount = story.trim() ? story.trim().split(/\s+/).length : 0;

  const validate = () => {
    const errs = {};
    if (!title.trim()) errs.title = 'Give your project a title';
    if (!field.trim()) errs.field = 'Select a field';
    if (!status) errs.status = 'How is it going?';
    if (wordCount < 20) errs.story = 'Write at least a few sentences';
    return errs;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSubmitted(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (submitted) {
    return (
      <main className={styles.main}>
        <div className={styles.successContainer}>
          <div className={styles.successCard}>
            <div className={styles.successEmoji}>🎉</div>
            <h1 className={styles.successTitle}>Your story is live on Labora!</h1>
            <p className={styles.successSub}>
              <strong>{title}</strong> is now part of the Labora community. Other scientists can find your work, follow your journey, and reach out to collaborate.
            </p>
            <div className={styles.successMeta}>
              <span className={styles.successField}>{field}</span>
              {status && (
                <span className={styles.successStatus}>
                  {statuses.find(s => s.value === status)?.emoji}{' '}
                  {statuses.find(s => s.value === status)?.label}
                </span>
              )}
            </div>
            <blockquote className={styles.successQuote}>
              "Every big discovery started exactly where you are right now — at the beginning, uncertain, curious. Keep going."
            </blockquote>
            <div className={styles.successActions}>
              <Link to="/portfolio" className={styles.successPrimary}>
                Explore other projects
              </Link>
              <Link to="/" className={styles.successSecondary}>
                Go to homepage
              </Link>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className={styles.main}>
      <div className={styles.glow} aria-hidden="true" />

      <div className={styles.container}>
        {/* ── Top bar ── */}
        <div className={styles.topBar}>
          <Link to="/" className={styles.logoLink}>
            <svg className={styles.logoIcon} viewBox="0 0 32 32" fill="none" aria-hidden="true">
              <path d="M11 4h10M13 4v9.5L6.5 22a5 5 0 004.3 7.5h10.4A5 5 0 0025.5 22L19 13.5V4"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="12.5" cy="22.5" r="1.5" fill="currentColor" opacity="0.7"/>
              <circle cx="18" cy="24.5" r="1" fill="currentColor" opacity="0.5"/>
              <circle cx="15" cy="20.5" r="1" fill="currentColor" opacity="0.6"/>
            </svg>
            <span className={styles.logoText}>Labora</span>
          </Link>
          {user && (
            <div className={styles.userBadge}>
              <div className={styles.userAvatar}>{user.fullName.charAt(0)}</div>
              <span className={styles.userName}>{user.fullName.split(' ')[0]}</span>
            </div>
          )}
        </div>

        {/* ── Page header ── */}
        <div className={styles.pageHeader}>
          <span className={styles.eyebrow}>Your first project</span>
          <h1 className={styles.headline}>Start your story.</h1>
          <p className={styles.intro}>
            You don't need a finished experiment. You don't need a hypothesis that works yet. Just write what you're working on — honestly, in your own words. This is your journal.
          </p>
        </div>

        {/* ── Writing form ── */}
        <form className={styles.form} onSubmit={handleSubmit} noValidate>

          {/* Project title */}
          <div className={styles.field}>
            <label className={styles.label} htmlFor="title">Project title</label>
            <input
              id="title"
              type="text"
              className={`${styles.titleInput} ${errors.title ? styles.inputError : ''}`}
              placeholder="What are you investigating?"
              value={title}
              onChange={e => { setTitle(e.target.value); if (errors.title) setErrors(er => ({...er, title: ''})); }}
            />
            {errors.title && <span className={styles.error}>{errors.title}</span>}
          </div>

          {/* Field + Status row */}
          <div className={styles.metaRow}>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="field">Science field</label>
              <input
                id="field"
                type="text"
                className={`${styles.input} ${errors.field ? styles.inputError : ''}`}
                placeholder="e.g. Environmental Science"
                value={field}
                onChange={e => { setField(e.target.value); if (errors.field) setErrors(er => ({...er, field: ''})); }}
              />
              {errors.field && <span className={styles.error}>{errors.field}</span>}
            </div>
          </div>

          {/* Status selector */}
          <div className={styles.field}>
            <label className={styles.label}>Current status</label>
            <div className={styles.statusGrid}>
              {statuses.map(s => (
                <button
                  key={s.value}
                  type="button"
                  className={`${styles.statusBtn} ${status === s.value ? styles.statusBtnActive : ''}`}
                  style={status === s.value ? { borderColor: s.color, background: `${s.color}12` } : {}}
                  onClick={() => { setStatus(s.value); if (errors.status) setErrors(er => ({...er, status: ''})); }}
                >
                  <span className={styles.statusEmoji}>{s.emoji}</span>
                  <span className={styles.statusLabel} style={status === s.value ? { color: s.color } : {}}>
                    {s.label}
                  </span>
                </button>
              ))}
            </div>
            {errors.status && <span className={styles.error}>{errors.status}</span>}
          </div>

          {/* Story textarea */}
          <div className={styles.field}>
            <div className={styles.storyLabelRow}>
              <label className={styles.label} htmlFor="story">Your story</label>
              <span className={`${styles.wordCount} ${wordCount >= WORD_GOAL ? styles.wordCountDone : ''}`}>
                {wordCount} {wordCount >= WORD_GOAL ? '✓' : `/ ${WORD_GOAL} words goal`}
              </span>
            </div>
            <textarea
              id="story"
              rows={14}
              className={`${styles.storyArea} ${errors.story ? styles.inputError : ''}`}
              placeholder="What happened in your lab today? A failure is just as valuable as a breakthrough — write it all down. What are you trying to figure out? What did you discover, even if it's not what you expected? What questions do you still have? This is your journal, not a paper. Write like you're telling a friend."
              value={story}
              onChange={e => { setStory(e.target.value); if (errors.story) setErrors(er => ({...er, story: ''})); }}
            />
            {errors.story && <span className={styles.error}>{errors.story}</span>}
          </div>

          {/* Submit */}
          <div className={styles.formFooter}>
            <p className={styles.footerNote}>
              Your project will be visible to the Labora community immediately after publishing.
            </p>
            <button type="submit" className={styles.publishBtn}>
              <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path d="M10 3v11M5 9l5-5 5 5" stroke="currentColor" strokeWidth="1.8"
                      strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Publish to Labora
            </button>
          </div>

        </form>
      </div>
    </main>
  );
};

export default DocumentProject;
